import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowUp, Clock } from 'lucide-react';
import { templatesByCategory } from '@/lib/workflowTemplates';
import { listWorkflows } from '@/lib/workflowStore';
import { PosterStrip } from '@/components/organisms/PosterStrip';
import { TemplatePreview } from '@/components/organisms/TemplatePreview';
import { examplesForTemplate } from '@/lib/workedExamples';
import styles from './Workflows.module.css';

/**
 * Workflows home.
 *
 * Two ways in, and the page is organised around that: describe what you want in
 * the composer, or open a template. Both land on the same creator screen.
 *
 * Templates and saved work are tabs rather than stacked sections. The page
 * previously ran five sections deep, and the longest of them — "Featured" —
 * was the same content twice: every featured tile was an instance of a template
 * shown again below it, in a different visual language. Folding the worked
 * example into its own template's tile removed the duplication and two
 * sections with it.
 *
 * Every tile is therefore a real analysis of a real subject, already bound. A
 * template that opens on rendered figures explains itself; one that opens on
 * "choose a title" asks the reader to already know what the product does.
 */

/* Rotating examples, so the placeholder teaches the shape of a good ask
   instead of the user having to guess it. */
const PROMPT_EXAMPLES = [
  'What is Stranger Things worth to Netflix?',
  'Which genres earn the most on Netflix?',
  'Compare Max and Peacock on churn',
  'How has Disney+ grown since 2020?',
];

const CATEGORY_BLURBS: Record<string, string> = {
  'Content Valuation': 'What a title is worth, and to whom',
  'Streaming Metrics': 'How platforms are performing, market by market',
};

type Tab = 'templates' | 'mine';

export function Workflows() {
  const navigate = useNavigate();
  const [search, setSearch] = useSearchParams();
  const [prompt, setPrompt] = useState('');
  const [exampleIndex, setExampleIndex] = useState(0);
  const saved = listWorkflows();
  const groups = templatesByCategory();

  /* Tab lives in the URL so returning from a canvas puts you back where you
     were. Defaults to templates rather than to whichever tab has content — a
     page that rearranges itself between visits is worse than one that is
     occasionally a click too deep. */
  const tab: Tab = search.get('tab') === 'mine' ? 'mine' : 'templates';
  const setTab = (next: Tab) => {
    const params = new URLSearchParams(search);
    if (next === 'templates') params.delete('tab');
    else params.set('tab', next);
    setSearch(params, { replace: true });
  };

  /* The placeholder cycles so the box teaches more than one shape of ask.
     Paused once the user types — a moving placeholder under a filled field is
     just motion, and it competes with what they are writing. */
  useEffect(() => {
    if (prompt) return;
    const timer = setInterval(
      () => setExampleIndex((i) => (i + 1) % PROMPT_EXAMPLES.length),
      4000
    );
    return () => clearInterval(timer);
  }, [prompt]);

  const start = () => {
    const seed = prompt.trim();
    navigate(seed ? `/workflows/new?prompt=${encodeURIComponent(seed)}` : '/workflows/new');
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    /* Enter sends; Shift+Enter breaks the line — the convention every chat
       surface has trained users on. */
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      start();
    }
  };

  return (
    <div className={styles.page}>
      <section className={styles.hero}>
        <h1 className={styles.heroTitle}>Create your workflow with Plexie AI</h1>
        <p className={styles.heroSub}>
          Describe the analysis you want. Plexie assembles it from live Parrot data.
        </p>

        <div className={styles.composer}>
          <textarea
            className={styles.composerInput}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={onKeyDown}
            rows={2}
            placeholder={`e.g. ${PROMPT_EXAMPLES[exampleIndex]}`}
            aria-label={'Describe your workflow'}
          />
          <div className={styles.composerFoot}>
            {/* Two, not three: a third chip pushes the row to wrap and strands
                the send button alone in the corner. The placeholder still
                cycles all four, so nothing is lost. */}
            <div className={styles.examples}>
              {PROMPT_EXAMPLES.slice(1, 3).map((example) => (
                <button
                  key={example}
                  type={'button'}
                  className={styles.example}
                  onClick={() => setPrompt(example)}
                >
                  {example}
                </button>
              ))}
            </div>
            <button
              type={'button'}
              className={styles.send}
              onClick={start}
              disabled={!prompt.trim()}
              aria-label={'Start workflow'}
            >
              <ArrowUp size={16} strokeWidth={2.5} />
            </button>
          </div>
        </div>
      </section>

      {/* ── Tabs ─────────────────────────────────────────────────────────── */}
      <div className={styles.tabs} role="tablist" aria-label="Workflows">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'templates'}
          className={`${styles.tab} ${tab === 'templates' ? styles.tabActive : ''}`}
          onClick={() => setTab('templates')}
        >
          Templates
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'mine'}
          className={`${styles.tab} ${tab === 'mine' ? styles.tabActive : ''}`}
          onClick={() => setTab('mine')}
        >
          My workflows
          {/* The count is what tells a returning user there is something here
              without making them open the tab to find out. */}
          {saved.length > 0 && <span className={styles.tabCount}>{saved.length}</span>}
        </button>
      </div>

      {tab === 'templates' ? (
        groups.map(({ category, templates }) => (
          <section key={category} className={styles.section}>
            <div className={styles.sectionHead}>
              <h2 className={styles.sectionTitle}>{category}</h2>
              <p className={styles.sectionSub}>{CATEGORY_BLURBS[category]}</p>
            </div>

            <div className={styles.grid}>
              {templates.map((template) => {
                const [hero, ...alternates] = examplesForTemplate(template.id);
                return (
                  <div key={template.id} className={styles.tile}>
                    <button
                      type="button"
                      className={styles.tileMain}
                      onClick={() =>
                        navigate(
                          hero
                            ? `/workflows/new?example=${hero.id}`
                            : `/workflows/new?template=${template.id}`
                        )
                      }
                    >
                      <span className={styles.tileImage}>
                        {/* Every template ships with a worked example today.
                            The wireframe is the fallback for one added later
                            without a subject bound yet. */}
                        {hero ? (
                          <PosterStrip subject={hero.subject} />
                        ) : (
                          <TemplatePreview
                            blocks={template.blocks}
                            category={template.category}
                          />
                        )}
                      </span>
                      <span className={styles.tileBody}>
                        <span className={styles.tileName}>{template.name}</span>
                        {hero && <span className={styles.tileSubject}>{hero.name}</span>}
                        <span className={styles.tileDesc}>{template.description}</span>
                      </span>
                    </button>

                    {/* Other subjects the same analysis has been run on. Keeps
                        Bluey and Star Trek reachable without giving either its
                        own tile — the analysis is the thing being chosen. */}
                    {alternates.length > 0 && (
                      <div className={styles.tileAlts}>
                        <span className={styles.tileAltsLabel}>Also:</span>
                        {alternates.map((alt) => (
                          <button
                            key={alt.id}
                            type="button"
                            className={styles.tileAlt}
                            onClick={() => navigate(`/workflows/new?example=${alt.id}`)}
                          >
                            {alt.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        ))
      ) : saved.length > 0 ? (
        <section className={styles.section}>
          <div className={styles.savedGrid}>
            {saved.map((workflow) => (
              <button
                key={workflow.id}
                type={'button'}
                className={styles.savedCard}
                onClick={() => navigate(`/workflows/${workflow.id}`)}
              >
                <span className={styles.savedName}>{workflow.name}</span>
                {/* The clock belongs to the date, not the block count — it sat
                    in front of both and labelled the wrong one. */}
                <span className={styles.savedMeta}>
                  <span>
                    {workflow.blocks.length} block{workflow.blocks.length === 1 ? '' : 's'}
                  </span>
                  <span className={styles.savedDot} aria-hidden="true">
                    ·
                  </span>
                  <span className={styles.savedDate}>
                    <Clock size={12} />
                    {new Date(workflow.updatedAt).toLocaleDateString()}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </section>
      ) : (
        <section className={styles.section}>
          <div className={styles.emptyState}>
            <h2 className={styles.emptyTitle}>Nothing saved yet</h2>
            <p className={styles.emptyText}>
              Workflows you save from the canvas land here. Open a template to get
              started, or describe what you want above.
            </p>
            <button
              type="button"
              className={styles.emptyBtn}
              onClick={() => setTab('templates')}
            >
              Browse templates
            </button>
          </div>
        </section>
      )}
    </div>
  );
}
